<script lang="ts">
import ActivityIndicator from '@/components/v2/ActivityIndicator.vue';
import AppButton from '@/components/v2/AppButton.vue';
import AppSelect from '@/components/v2/AppSelect.vue';
import Toggle from '@/components/v2/Toggle.vue';
import Tooltip from '@/components/v2/Tooltip.vue';
import { AlertTriangle, Pencil, Trash2, X, Download, RotateCcw, ShieldCheck } from '@lucide/vue';
import CreateUpdateUserModal from '@/views/admin/PermissionAdmin/CreateUpdateUserModal.vue';

export default {
  name: 'PermissionAdmin',
  components: {
    ActivityIndicator,
    AppButton,
    AppSelect,
    Toggle,
    Tooltip,
    Pencil,
    Trash2,
    X,
    AlertTriangle,
    Download,
    RotateCcw,
    ShieldCheck,
    CreateUpdateUserModal,
  },
};
</script>

<script setup lang="ts">
import { permissionService } from '@/services/api';
import { ref, onMounted, computed, watch } from 'vue';
import { AllowedPermissionsByRole, AWSUserStatus, UserPermissionDetails, UserPermissionDetailsWithRoles } from '@/types/UserPermissions';
import { UserRoles } from '@/types/UserPermissions';
import AppModal from '@/components/v2/AppModal.vue';
import { UserModalType, UserType } from '@/types/User';
import { Commit } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';
import userService from '@/services/UserService';
import { clientErrorMessage } from '@/utilities/Errors';

const SECURITY_ROLES = ['admin', 'broker', 'client', 'market'];

// DATA
// ------------------------------------------------
const userData = ref<UserPermissionDetails[]>([]);
const roles = ref<UserRoles[]>([]);
const allowedPermissionsByRole = ref<AllowedPermissionsByRole[]>([]);
const loading = ref(false);
const updatingUser = ref(false);
const updatingPassword = ref(false);
const showCreateUserModal = ref(false);
const showUpdateUserModal = ref(false);
const showPasswordResetModal = ref(false);
const showMfaResetModal = ref(false);
const resettingMfa = ref(false);
const showDeleteUserModal = ref(false);

const searchQuery = ref<string>('');
const selectedUserType = ref<string | null | undefined>();
const showDeletedUsers = ref<boolean>(false);
const showRestoreUserModal = ref(false);

//// role change modal
const userSelectedRole = ref<string>();
const selectedUser = ref<UserPermissionDetailsWithRoles | undefined>();
const userPermissions = ref<string[]>([]);
const userSelectedPermissions = ref<string[]>([]);

// COMPUTED
// ------------------------------------------------
const users = computed<UserPermissionDetailsWithRoles[]>(() => {
  return userData.value
    .map((user: UserPermissionDetails) => {
      const uniqueAccounts = uniqueAccountsForUser(user) ?? [];
      const userData = {
        ...user,
        //sort accounts parent first then alpha
        accounts: uniqueAccounts?.sort((a, b) => {
          if (a.parent && !b.parent) return -1;
          if (!a.parent && b.parent) return 1;
          if (a.accountName < b.accountName) return -1;
          if (a.accountName > b.accountName) return 1;
          return 0;
        }),
        roles: roles.value?.find((role) => role.userSubId === user.userSubId)?.roles?.filter((role) => SECURITY_ROLES.includes(role)) ?? [],
        isAdmin: false,
      };

      userData.isAdmin = userData.roles.includes('admin');
      return userData;
    })
    .filter((user) => {
      // this needs to be a fuzzy search across email, name, roles, and accounts
      if (searchQuery.value) {
        return (
          user.userEmail.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
          user.name?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
          user.roles.join().toLowerCase().includes(searchQuery.value.toLowerCase())
        );
      }
      return true;
    })
    .filter((user) => {
      if (selectedUserType.value) {
        return user.userTypeName === selectedUserType.value;
      }
      return true;
    })
    .filter((user) => {
      // Filter out deleted users unless showDeletedUsers is true
      if (!showDeletedUsers.value && user.deletedAt) {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      // sort on usertypeid, then user email
      if (a.userTypeId < b.userTypeId) return 1;
      if (a.userTypeId > b.userTypeId) return -1;
      if (a.userEmail < b.userEmail) return -1;
      if (a.userEmail > b.userEmail) return 1;
      return 0;
    });
});

const userTypes = computed(() => {
  return (
    userData.value?.reduce((acc, user) => {
      if (user.userTypeName && !acc.includes(user.userTypeName)) {
        acc.push(user.userTypeName);
      }
      return acc;
    }, [] as string[]) ?? []
  );
});

const userTypeFilterOptions = computed(() => [{ value: null, label: 'All Users' }, ...userTypes.value.map((ut) => ({ value: ut, label: ut }))]);

/**
 * Picks the first role from the array of SECURITY_ROLES.
 * The user could have multiple cognito roles, but we are moving to a single role per user
 * with the new permissions model.
 */
const userCurrentRole = computed<string>(() => {
  if (selectedUser.value) {
    for (const role of SECURITY_ROLES) {
      if (selectedUser.value?.roles.includes(role)) {
        return role;
      }
    }
  }

  return '';
});

// WATCHERS
// ---------
watch(userSelectedRole, () => {
  if (!userSelectedRole.value) {
    return;
  }

  // reset the selected permissions when the role changes
  if (userCurrentRole.value !== userSelectedRole.value) {
    userSelectedPermissions.value = [];
  } else {
    userSelectedPermissions.value = userPermissions.value;
  }
});

// METHODS
// ------------------------------------------------
async function refresh() {
  loading.value = true;
  [userData.value, roles.value, allowedPermissionsByRole.value] = await Promise.all([
    await permissionService.GetUserPermissionsV2(showDeletedUsers.value),
    await permissionService.GetAllUserRolesV2(),
    await permissionService.getAllowedPermissionsByRole(),
  ]);
  loading.value = false;
}

function uniqueAccountsForUser(user: UserPermissionDetails) {
  const uniqueAccountMap = new Map();
  user.accounts?.forEach((account) => {
    if (!uniqueAccountMap.has(account.accountId)) {
      uniqueAccountMap.set(account.accountId, account);
    }
  });
  return Array.from(uniqueAccountMap.values());
}

async function exportUserPermissions() {
  const seriesNames = ['User Type', 'Name', 'Email', 'Last Login Date', 'Roles', 'Permissions', 'MFA Enrolled', 'Accounts'];
  const header = seriesNames.join(',');

  const csvRows = [header];
  userData.value.forEach((user) => {
    const userRoles = roles.value.find((u) => u.userSubId === user.userSubId);
    const joinedRoles = `"${userRoles?.roles?.join(', ')}"`;
    const permissions = `"${user.permissions?.join(', ')}"`;
    const mfa = user.mfaEnabled ? 'Yes' : 'No';
    const accounts = `"${user.accounts?.map((a) => a.accountName).join(', ') ?? ''}"`;
    const row = [user.userTypeName, user.name, user.userEmail, user.activeDate, joinedRoles, permissions, mfa, accounts];
    csvRows.push(row.join(','));
  });

  const csvContent = csvRows.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'User_Permissions.csv';
  a.click();
}

function formatRoles(roles?: string[]): string | undefined {
  if (!roles) return undefined;
  return roles
    .filter((role) => role !== 'admin')
    .map((role) =>
      role
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
    )
    .join(', ');
}

function formatPermissions(permissions?: string[]): string[][] {
  if (!permissions) return [];
  const permissionGroups = permissions
    .map((p) => p.split(':'))
    .reduce(
      (acc, [name, action]) => {
        if (!acc[action]) {
          acc[action] = [];
        }
        acc[action].push(name);
        return acc;
      },
      {} as Record<string, string[]>,
    );

  return Object.entries(permissionGroups).map(([action, permissions]) => [action, permissions.sort((a, b) => a.localeCompare(b)).join(', ')]);
}

function editUser(user: UserPermissionDetailsWithRoles) {
  handleOpenUpdateUserModal(user);
}

function handleOpenDeleteModal(user: UserPermissionDetailsWithRoles) {
  selectedUser.value = user;
  showDeleteUserModal.value = true;
}

function handleOpenUpdateUserModal(user: UserPermissionDetailsWithRoles) {
  const userPerms = user.permissions ?? [];
  if (userPerms) {
    userPermissions.value = userPerms;
    userSelectedPermissions.value = userPerms;
  }

  selectedUser.value = user;
  showUpdateUserModal.value = true;
  userSelectedRole.value = userCurrentRole.value;
}

function setSearchQuery(event: Event) {
  searchQuery.value = (event.target as HTMLInputElement).value;
}

function handleUserCreated(hasError: boolean, message?: string) {
  if (!hasError) {
    handleCloseUserModal();
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'User Created' });
    refresh();
  } else {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: message ?? 'Failed to Create User', color: 'error' });
  }
}

function handleCloseUserModal() {
  showUpdateUserModal.value = false;
  showCreateUserModal.value = false;
  selectedUser.value = undefined;
  userSelectedRole.value = undefined;
}

function handleCloseDeleteModal() {
  showDeleteUserModal.value = false;
  selectedUser.value = undefined;
  userSelectedRole.value = undefined;
}

function handleUserUpdated(hasError: boolean, message?: string) {
  handleCloseUserModal();

  if (!hasError) {
    refresh();
  } else {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: message ?? 'Failed to Update User', color: 'error' });
  }
}

//// password reset modal
async function handleResetUserPassword(user?: UserPermissionDetailsWithRoles) {
  if (!user?.userId) return;

  try {
    updatingPassword.value = true;
    await userService.resetUserPassword(user.userId);
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'User Password Reset' });
  } catch (e) {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Failed to reset password', color: 'error' });
  } finally {
    showPasswordResetModal.value = false;
    showUpdateUserModal.value = false;
    updatingPassword.value = false;
    await refresh();
  }
}

//// mfa reset modal
async function handleResetUserMfa(user?: UserPermissionDetailsWithRoles) {
  if (!user?.userId) return;

  try {
    resettingMfa.value = true;
    await userService.resetUserMfa(user.userId);
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: `MFA reset. ${user.userEmail} will set it up again at next sign-in.` });
  } catch (e) {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: clientErrorMessage(e) ?? "MFA reset didn't finish. Try again.", color: 'error' });
  } finally {
    showMfaResetModal.value = false;
    showUpdateUserModal.value = false;
    resettingMfa.value = false;
    await refresh();
  }
}

//// delete user modal
async function deleteUser(user: UserPermissionDetails) {
  if (user.userTypeId === UserType.Manual) {
    try {
      updatingUser.value = true;
      await permissionService.DeleteUser(user.userSubId);
      Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'User Deleted' });
    } catch (e) {
      Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Failed to delete user', color: 'error' });
    } finally {
      handleCloseDeleteModal();
      updatingUser.value = false;
      await refresh();
    }
  }
}

function handleOpenRestoreModal(user: UserPermissionDetailsWithRoles) {
  selectedUser.value = user;
  showRestoreUserModal.value = true;
}

function handleCloseRestoreModal() {
  showRestoreUserModal.value = false;
  selectedUser.value = undefined;
}

async function restoreUser(user: UserPermissionDetails) {
  try {
    updatingUser.value = true;
    await permissionService.RestoreUser(user.userSubId);
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'User Restored' });
  } catch (e) {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Failed to restore user', color: 'error' });
  } finally {
    handleCloseRestoreModal();
    updatingUser.value = false;
    await refresh();
  }
}

// LIFECYCLE HOOKS
// ==================================================
onMounted(async () => {
  await refresh();
});
</script>

<template>
  <div class="inner flex h-full flex-col items-center p-4">
    <div
      v-if="loading"
      class="mt-[8%] flex w-full justify-around">
      <span class="loading loading-lg loading-spinner"></span>
    </div>

    <div
      v-else
      class="flex h-full w-full flex-col md:w-full lg:w-[90%] 2xl:w-[80%]">
      <div class="flex justify-between">
        <h2 class="mb-2 ml-2">Manage User Permissions</h2>
      </div>

      <!-- FILTERS -->
      <div class="mb-2 flex flex-row items-center justify-between gap-4 px-1">
        <div class="relative flex w-1/3 flex-row">
          <input
            type="text"
            placeholder="Search"
            @input="setSearchQuery"
            :value="searchQuery"
            class="input h-10 w-full rounded-md border-solid" />
          <button
            v-if="searchQuery"
            @click="searchQuery = ''"
            class="absolute top-2 right-1 text-slate-400 hover:text-slate-700">
            <X />
          </button>
        </div>

        <AppSelect
          v-model="selectedUserType"
          :options="userTypeFilterOptions"
          placeholder="All Users" />

        <div class="mr-auto ml-0">
          <Toggle
            id="show-deleted-users"
            label="Show Deleted Users"
            v-model="showDeletedUsers"
            @update:modelValue="refresh"
            size="sm" />
        </div>

        <AppButton
          primary
          @click="showCreateUserModal = true">
          Create User
        </AppButton>

        <Tooltip
          text="Export All as .CSV"
          position="top"
          class="mr-2 flex">
          <AppButton @click="exportUserPermissions">
            <Download :size="15" />
          </AppButton>
        </Tooltip>
      </div>

      <!-- USER TABLE -->
      <table class="table mb-[33vh] w-full table-auto border border-solid border-slate-200 table-sm pt-0.5 font-medium text-slate-800">
        <thead class="sticky top-[64px] z-50 shadow-xs">
          <tr class="h-8 bg-white">
            <th class="mx-0 px-0"></th>
            <th class="px-0 text-center">User Type</th>
            <th>Email</th>
            <th>Name</th>
            <th>Roles</th>
            <th>Permissions</th>
            <th class="text-center">MFA Enrolled</th>
            <th class="">Accounts</th>
            <th class="pl-3.5">Activity</th>
            <th class="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="user in users"
            :key="user.userSubId"
            class="group h-12"
            :class="{ 'opacity-50': user.deletedAt }">
            <td class="mx-0 w-4 pr-0 pl-3">
              <div
                v-if="user.userStatus === AWSUserStatus.FORCE_CHANGE_PASSWORD"
                class="tooltip tooltip-bottom flex"
                data-tip="Password Reset Requested">
                <AlertTriangle
                  :size="18"
                  class="stroke-red-400" />
              </div>
            </td>
            <td class="px-0 text-center">{{ user.userTypeName }}</td>
            <td>{{ user.userEmail }}</td>
            <td>{{ user.name || '-' }}</td>

            <!-- ROLES -->
            <td class="font-normal">
              <span
                v-if="user.isAdmin"
                class="font-bold text-amber-700">
                Admin<span
                  v-if="user.roles.length > 1"
                  class="text-black"
                  >,
                </span>
              </span>
              <span>{{ formatRoles(user.roles) || (user.isAdmin ? '' : '-') }}</span>
            </td>

            <!-- PERMISSIONS -->
            <td>
              <div
                class="flex flex-col gap-1"
                v-if="formatPermissions(user.permissions).length > 0">
                <span
                  v-for="permsByType in formatPermissions(user.permissions)"
                  :key="permsByType[0]">
                  <span
                    v-for="(perm, index) in permsByType"
                    :key="perm">
                    <span
                      v-if="index === 0"
                      class="font-normal"
                      >{{ perm }}:
                    </span>
                    <span
                      v-else
                      class="font-bold"
                      >{{ perm }}</span
                    >
                  </span>
                </span>
              </div>
              <span v-else>-</span>
            </td>

            <!-- MFA -->
            <td class="text-center">
              <Tooltip
                v-if="user.mfaEnabled"
                text="MFA enrolled"
                position="top">
                <ShieldCheck
                  :size="18"
                  class="inline stroke-green-600" />
              </Tooltip>
            </td>

            <!-- Accounts -->
            <td>
              <template v-if="user.accounts && user.accounts.length > 0">
                <div
                  v-if="user.accounts && user.accounts.length > 1"
                  class="dropdown-hover dropdown dropdown-left cursor-pointer">
                  <div>
                    <a class="font-bold text-secondary">
                      {{ user.accounts.length + ' ' + (user.accounts.length > 1 ? 'Accounts' : 'Account') }}
                    </a>
                  </div>
                  <ul class="dropdown-content menu z-1 max-h-96 w-80 flex-nowrap gap-1 overflow-y-auto rounded-lg bg-base-100 p-2 text-xs shadow-sm">
                    <li
                      v-for="account in user.accounts"
                      :key="account.accountId"
                      class="flex flex-row rounded border border-solid border-base-200">
                      <span class="flex grow justify-between pl-2">
                        <span>{{ account.accountName }}</span>
                        <span
                          v-if="account.parent"
                          class="ml-2 inline-flex h-5 w-fit self-center rounded-xl bg-blue-200 px-2 align-baseline text-[8pt] text-blue-900">
                          <div class="self-center">Parent</div>
                        </span>
                      </span>
                    </li>
                  </ul>
                </div>

                <div
                  v-if="user.accounts && user.accounts.length === 1"
                  class="dropdown-hover dropdown dropdown-left cursor-pointer">
                  <div
                    class="max-w-36 overflow-hidden text-ellipsis whitespace-nowrap"
                    :title="`${user.accounts[0].accountName}`">
                    {{ user.accounts[0].accountName }}
                  </div>
                </div>
              </template>
              <div v-else>-</div>
            </td>

            <!-- Activity -->
            <td>
              <ActivityIndicator
                v-if="user.activeDate"
                :last-active-date="user.activeDate"></ActivityIndicator>
              <span
                v-else
                class="ml-2"
                >-</span
              >
            </td>

            <!-- Actions-->
            <td class="text-center">
              <div class="flex justify-center gap-1 *:flex *:items-center *:rounded-sm">
                <button
                  @click="editUser(user)"
                  class="p-2 opacity-20 transition-all group-hover:opacity-100 hover:bg-slate-200 hover:text-cyan-400">
                  <Pencil :size="16" />
                </button>
                <button
                  v-if="user.userTypeId === UserType.Manual && !user.deletedAt"
                  @click="handleOpenDeleteModal(user)"
                  class="p-2 opacity-20 transition-all group-hover:opacity-100 hover:bg-slate-200 hover:text-red-400"
                  title="Delete User">
                  <Trash2 :size="16"></Trash2>
                </button>
                <button
                  v-if="user.userTypeId === UserType.Manual && user.deletedAt"
                  @click="handleOpenRestoreModal(user)"
                  class="p-2 opacity-20 transition-all group-hover:opacity-100 hover:bg-slate-200 hover:text-green-400"
                  title="Restore User">
                  <RotateCcw :size="16"></RotateCcw>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <CreateUpdateUserModal
      v-if="(showUpdateUserModal && selectedUser) || showCreateUserModal"
      :show="showUpdateUserModal || showCreateUserModal"
      :user="selectedUser ?? undefined"
      :permissions="allowedPermissionsByRole"
      :type="showCreateUserModal ? UserModalType.Create : UserModalType.Update"
      :sso="(selectedUser && selectedUser.userTypeId === UserType.SSO) ?? false"
      @cancel="handleCloseUserModal"
      @updated="handleUserUpdated"
      @reset-password="showPasswordResetModal = true"
      @reset-mfa="showMfaResetModal = true"
      @created="handleUserCreated" />

    <!-- PASSWORD RESET MODAL -->
    <AppModal
      v-if="showPasswordResetModal && selectedUser"
      :show="showPasswordResetModal"
      action-button-label="Reset"
      :handle-cancel="() => (showPasswordResetModal = false)"
      :handle-action="() => handleResetUserPassword(selectedUser)"
      :loading="updatingPassword">
      <template v-slot:title>
        <h2 class="font-normal text-red-500">Reset Password</h2>
      </template>
      <template v-slot:content>
        <p class="text-md">
          Are you sure you want to reset the password for
          <span class="text-red-500">{{ selectedUser.userEmail }}</span
          >?
        </p>
      </template>
    </AppModal>

    <!-- MFA RESET MODAL -->
    <AppModal
      v-if="showMfaResetModal && selectedUser"
      :show="showMfaResetModal"
      action-button-label="Reset MFA"
      :handle-cancel="() => (showMfaResetModal = false)"
      :handle-action="() => handleResetUserMfa(selectedUser)"
      :loading="resettingMfa">
      <template v-slot:title>
        <h2 class="font-normal text-red-500">Reset MFA</h2>
      </template>
      <template v-slot:content>
        <p class="text-md">
          Reset multi-factor authentication for
          <span class="text-red-500">{{ selectedUser.userEmail }}</span
          >? Their phone number and authenticator app are removed and they set up MFA again at next sign-in. Sessions they already have can last up to
          an hour.
        </p>
      </template>
    </AppModal>

    <!-- DELETE USER MODAL -->
    <AppModal
      v-if="showDeleteUserModal && selectedUser"
      :show="showDeleteUserModal"
      action-button-label="Delete"
      :handle-cancel="handleCloseDeleteModal"
      :handle-action="() => selectedUser && deleteUser(selectedUser)"
      :loading="updatingUser">
      <template v-slot:title>
        <h2 class="font-normal text-red-500">Delete User</h2>
      </template>
      <template v-slot:content>
        <p class="text-md text-center">
          Are you sure you want to delete user
          <span class="text-red-500">{{ selectedUser.userEmail }}</span
          >? This will disable their account access.
        </p>
      </template>
    </AppModal>

    <!-- RESTORE USER MODAL -->
    <AppModal
      v-if="showRestoreUserModal && selectedUser"
      :show="showRestoreUserModal"
      action-button-label="Restore"
      :handle-cancel="handleCloseRestoreModal"
      :handle-action="() => selectedUser && restoreUser(selectedUser)"
      :loading="updatingUser">
      <template v-slot:title>
        <h2 class="font-normal text-green-500">Restore User</h2>
      </template>
      <template v-slot:content>
        <p class="text-md text-center">
          Are you sure you want to restore user
          <span class="text-green-500">{{ selectedUser.userEmail }}</span
          >? This will re-enable their account access.
        </p>
      </template>
    </AppModal>
  </div>
</template>

<style scoped></style>
